import { useEffect, useRef, useState } from "react";
import { adminUploadLogo, adminDeleteLogo, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { UploadSimple, Trash } from "@phosphor-icons/react";
import AdminCard from "../components/AdminCard";
import AdminPrimaryButton from "../components/AdminPrimaryButton";

export default function BrandingTab({ onSaved }) {
  const { settings, refreshSettings } = useAuth();
  const [preview, setPreview] = useState(null);
  const [userPicked, setUserPicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!userPicked) setPreview(settings?.logo_url || null);
  }, [settings?.logo_url, userPicked]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 1_000_000) { toast.error("Image must be under 1 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => { setPreview(reader.result); setUserPicked(true); };
    reader.readAsDataURL(file);
  };

  const upload = async () => {
    if (!preview || !preview.startsWith("data:image/")) { toast.error("Pick a logo first"); return; }
    setBusy(true);
    try {
      await adminUploadLogo(preview);
      setUserPicked(false);
      toast.success("Logo updated");
      await refreshSettings();
      await onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await adminDeleteLogo();
      setPreview(null);
      setUserPicked(false);
      toast.success("Logo removed");
      await refreshSettings();
      await onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="branding-tab">
      <AdminCard>
        <div className="p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Site logo</p>
          <h3 className="text-lg font-semibold text-slate-900 mt-1 mb-1">Upload a custom logo</h3>
          <p className="text-sm text-slate-500 mb-5">
            Recommended: SVG or PNG, transparent background, height around 32–64px. Max 1 MB.
          </p>

          <div className="flex items-center gap-6 flex-wrap">
            <div
              className="border-2 border-dashed border-slate-200 rounded-xl bg-slate-50 p-6 inline-flex items-center justify-center min-w-[160px] min-h-[80px]"
              data-testid="logo-preview"
            >
              {preview ? (
                <img src={preview} alt="Logo preview" className="h-12 w-auto max-w-[200px] object-contain" />
              ) : (
                <span className="text-xs text-slate-400 uppercase tracking-wider">No logo</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={onFile}
                className="hidden"
                data-testid="logo-file-input"
              />
              <button
                onClick={() => inputRef.current?.click()}
                className="border border-slate-300 hover:bg-slate-50 rounded-lg px-5 py-2.5 text-sm font-medium text-slate-700 inline-flex items-center gap-2 transition-colors"
                data-testid="logo-choose-button"
              >
                <UploadSimple size={14} weight="bold" /> Choose file
              </button>
              <AdminPrimaryButton
                onClick={upload}
                busy={busy}
                busyLabel="Saving…"
                disabled={!preview}
                data-testid="logo-upload-button"
              >
                Save logo
              </AdminPrimaryButton>
              {settings?.logo_url && (
                <button
                  onClick={remove}
                  disabled={busy}
                  className="text-slate-400 hover:text-red-600 px-1 py-2 text-sm inline-flex items-center gap-2 transition-colors"
                  data-testid="logo-remove-button"
                >
                  <Trash size={12} /> Remove current logo
                </button>
              )}
            </div>
          </div>
        </div>
      </AdminCard>
    </div>
  );
}
